import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  ListItem,
  IconButton,
  useMediaQuery,
  Grid,
} from '@mui/material';
import Check from '../../assets/images/acConnex/CheckIcon.png';

// Replace old constants
const DESKTOP_CARD_WIDTH = 378.67;
const TABLET_CARD_WIDTH = 378.67;
const MOBILE_CARD_WIDTH = 272;
const SMALL_MOBILE_CARD_WIDTH = 272;
const CARD_MARGIN = 28;
const SMALL_CARD_MARGIN = 16;
const PARTIAL_CARD_VISIBLE = 0.25; // Show 1/4 of the next card

interface FeatureItem {
  id: number;
  text: string;
}

interface CardData {
  id: number;
  title: string;
  logo: string;
  alt: string;
  features: FeatureItem[];
}

interface CarouselData {
  id: number;
  heading: string;
  image: {
    alt: string;
    title: string;
    url: string;
  };
  cards: {
    id: number;
    heading: string;
    image: {
      alt: string;
      title: string;
      url: string;
    };
    list: string;
  }[];
}

interface FeaturesCarouselProps {
  data: CarouselData[];
}

interface FeatureCardProps extends CardData {
  cardWidth: number;
  isFirst?: boolean;
  isLast?: boolean;
  headingId?: string;
  tabIndex?: number;
  'aria-hidden'?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  position: any;
  total: number;
}

const FeatureCard = React.forwardRef<HTMLLIElement, FeatureCardProps>(
  (
    {
      id,
      title,
      logo,
      alt,
      features,
      tabIndex = 0,
      onKeyDown,
      position,
      total,
      cardWidth,
      isLast = false
    },
    ref
  ) => {
    const featureRefs = React.useRef<Array<HTMLLIElement | null>>([]);

    const focusFeature = (idx: number) => {
      requestAnimationFrame(() => featureRefs.current[idx]?.focus());
    };

    const handleCardKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown' && features.length) {
        e.preventDefault();
        focusFeature(0);
      }
    };

    const handleFeatureKeyDown = (e: React.KeyboardEvent, idx: number) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx + 1 < features.length ? idx + 1 : 0;
        focusFeature(next);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx - 1 >= 0 ? idx - 1 : features.length - 1;
        focusFeature(prev);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        (ref as React.RefObject<HTMLLIElement>)?.current?.focus();
      }
    };

    return (
      <ListItem
        ref={ref}
        onKeyDown={handleCardKeyDown}
        aria-labelledby={`card-title-${id}`}
        sx={{
          minWidth: cardWidth,
          maxWidth: cardWidth,
          minHeight: 350,
          mr: isLast ? 0 : `${CARD_MARGIN}px`,
          flexShrink: 0,
          borderRadius: '16px',
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          listStyle: 'none',
          boxShadow: '0px 0px 15px 0px #00000026 !important',
          position: 'relative',
          '&:focus-visible': {
            outline: '3px solid #1460AA',
            outlineOffset: '4px'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 2, pt: 3, alignSelf: 'flex-start', pl: '28px' }}>
          <img
            src={logo}
            alt={alt}
            style={{
              width: 24,
              height: 24,
              marginBottom: 16,
              objectFit: 'contain'
            }}
          />
          <Typography
            id={`card-title-${id}`}
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px'
            }}
          >
            {title}
            <Box
              component="span"
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                p: 0,
                m: -1,
                // overflow: 'hidden',
                clip: 'rect(0 0 0 0)'
              }}
            >
              {`${position} of ${total}`}
            </Box>
          </Typography>
        </Box>

        {/* Features (not tabbable initially) */}
        <Box
          component="ul"
          aria-label={`${features.length} features`}
          sx={{
            mt: 1,
            p: 0,
            pl: 3,
            pr: 3,
            pb: 3,
            flex: 1,
            listStyle: 'none',
            margin: 0
          }}
        >
          {features.map((feature, idx) => (
            <Box
              key={feature.id}
              component="li"
              role="listitem"
              ref={(el: HTMLLIElement | null) => {
                featureRefs.current[idx] = el;
              }}
              aria-posinset={idx + 1}
              aria-setsize={features.length}
              onKeyDown={(e) => handleFeatureKeyDown(e, idx)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                py: 0.5,
                outline: 'none',
                position: 'relative',
                '&:focus-visible': {
                  outline: '2px solid #1460AA',
                  borderRadius: '4px'
                }
              }}
            >
              <Box sx={{ minWidth: 32, mr: 0.5, mt: 0.25, ml: 0.5 }}>
                <img
                  src={Check}
                  alt=""
                  aria-hidden="true"
                  style={{ width: 16, height: 16 }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#101828',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px'
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0',
                    whiteSpace: 'nowrap',
                    border: 0
                  }}
                >
                  •
                </span>
                <span dangerouslySetInnerHTML={{ __html: feature.text }} />
              </Typography>
            </Box>
          ))}
        </Box>
      </ListItem>
    );
  }
);

FeatureCard.displayName = 'FeatureCard';

const getFullImageUrl = (url: string): string => {
  const env = process.env['REACT_APP_ENV_LOADED'];
  let baseUrl = `https://content-ndc-portal.aircanada.com`;
  if (env === 'DEV' || env === 'INT' || env === 'CRT') {
    baseUrl = `https://content-ndc-portal-int.aircanada.com`;
  } else if (env === 'BAT' || env === 'PREPROD') {
    baseUrl = `https://content-ndc-portal-bat.aircanada.com`;
  }
  return baseUrl + url;
};

const removeHtmlTags = (html: string) => html?.replace(/<[^>]*>/g, '') || '';

const parseListItems = (html: string): string[] => {
  const matches = html.match(/<li>([\s\S]*?)<\/li>/g) || [];
  return matches.map((li) => li.replace(/^<li>|<\/li>$/g, ''));
};

const SingleCarousel: React.FC<{ carouselData: CarouselData }> = ({
  carouselData
}) => {
  const [startIndex, setStartIndex] = useState(0);
  // NEW drag states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [actualViewportWidth, setActualViewportWidth] = useState(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = `carousel-heading-${carouselData.id}`;
  const regionId = `carousel-region-${carouselData.id}`;

  const prevLabel = 'Previous card';
  const nextLabel = 'Next card';
  const isMobile = useMediaQuery('(max-width: 600px)');
  const isSmallMobile = useMediaQuery('(max-width: 450px)');
  const isTablet = useMediaQuery('(min-width:601px) and (max-width:1023px)');
  const VISIBLE_CARDS = isMobile ? 1 : isTablet ? 2 : 3;
  let cardWidth: number;
  let cardMargin: number;

  if (isSmallMobile) {
    cardWidth = SMALL_MOBILE_CARD_WIDTH;
    cardMargin = SMALL_CARD_MARGIN;
  } else if (isMobile) {
    cardWidth = MOBILE_CARD_WIDTH;
    cardMargin = CARD_MARGIN;
  } else if (isTablet) {
    cardWidth = TABLET_CARD_WIDTH;
    cardMargin = CARD_MARGIN;
  } else {
    cardWidth = DESKTOP_CARD_WIDTH;
    cardMargin = CARD_MARGIN;
  }
  // Transform carousel data to card data
  const cardData: CardData[] = carouselData.cards.map((card) => ({
    id: card.id,
    title: removeHtmlTags(card.heading),
    logo: getFullImageUrl(card.image.url),
    alt: card.image.alt,
    features: parseListItems(card.list).map((text, index) => ({
      id: index,
      text
    }))
  }));

  const totalCards = cardData.length;

  // Calculate viewport width to show exactly 3 cards + 0.25 of the 4th card
  // On mobile, adjust to prevent overflow
  const calculatedViewportWidth = isMobile
    ? cardWidth + cardMargin
    : VISIBLE_CARDS * (cardWidth + cardMargin) +
      cardWidth * PARTIAL_CARD_VISIBLE;

  // Use actual measured viewport width if available, otherwise use calculated
  const viewportWidth =
    actualViewportWidth > 0 ? actualViewportWidth : calculatedViewportWidth;

  const cardOffset = cardWidth + cardMargin;

  // Calculate how many cards actually fit in the viewport based on width
  const cardsPerViewport = viewportWidth / cardOffset;

  // Allow scrolling to show all remaining cards, ensuring the last card is fully visible
  // We need to scroll far enough so the last card is completely visible in the viewport
  const maxShift = Math.max(0, totalCards - Math.floor(cardsPerViewport));

  // Check if there are remaining cards that don't fit and enable scrolling for them
  const hasRemainingCards = totalCards > Math.floor(cardsPerViewport);

  // Measure actual viewport width on mount and window resize
  React.useEffect(() => {
    if (!scrollContainerRef.current) return;

    // Use ResizeObserver to measure actual container width
    const resizeObserver = new ResizeObserver(() => {
      if (scrollContainerRef.current) {
        setActualViewportWidth(scrollContainerRef.current.clientWidth);
      }
    });

    resizeObserver.observe(scrollContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  // Handle keyboard navigation for cards
  const handleCardKeyDown = (event: React.KeyboardEvent, cardIndex: number) => {
    const visibleStartIndex = startIndex;
    const visibleEndIndex = startIndex + VISIBLE_CARDS - 1;
    const isFirstVisible = cardIndex === visibleStartIndex;
    const isLastVisible =
      cardIndex === Math.min(visibleEndIndex, totalCards - 1);

    if (event.key === 'Tab') {
      if (!event.shiftKey && isLastVisible) {
        // Tab from last visible card - focus next button if available, otherwise let focus move naturally
        if (startIndex < maxShift && hasRemainingCards) {
          event.preventDefault();
          nextButtonRef.current?.focus();
        }
        // If no next button, let focus move to next section naturally
      } else if (event.shiftKey && isFirstVisible) {
        // Shift+Tab from first visible card - focus prev button if available, otherwise let focus move naturally
        if (startIndex > 0 && hasRemainingCards) {
          event.preventDefault();
          prevButtonRef.current?.focus();
        }
        // If no prev button, let focus move to previous section naturally
      }
    }
  };

  // Handle keyboard navigation for navigation buttons
  const handleNavButtonKeyDown = (
    event: React.KeyboardEvent,
    buttonType: 'prev' | 'next'
  ) => {
    if (event.key === 'Tab') {
      if (buttonType === 'prev' && !event.shiftKey) {
        // Tab from prev button - focus first visible card
        event.preventDefault();
        focusCardByIndex(startIndex);
      } else if (buttonType === 'next' && event.shiftKey) {
        // Shift+Tab from next button - focus last visible card
        event.preventDefault();
        const lastVisibleIndex = Math.min(
          startIndex + VISIBLE_CARDS - 1,
          totalCards - 1
        );
        focusCardByIndex(lastVisibleIndex);
      }
    }
  };

  const handleNext = () => {
    setDragOffset(0);
    setStartIndex((prevIndex) => {
      // Move VISIBLE_CARDS at a time, stop when reaching maxShift
      const newIndex = Math.min(prevIndex + VISIBLE_CARDS, maxShift);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const targetCardIndex = Math.min(
            newIndex + VISIBLE_CARDS - 1,
            totalCards - 1
          );
          const targetCard = cardRefs.current[targetCardIndex];
          if (targetCard) {
            targetCard.setAttribute('aria-live', 'assertive');
            targetCard.focus();
            setTimeout(() => {
              targetCard.setAttribute('aria-live', 'off');
            }, 100);
          }
        }, 50);
      });
      return newIndex;
    });
  };

  const handlePrev = () => {
    setDragOffset(0);
    setStartIndex((prevIndex) => {
      // Move VISIBLE_CARDS at a time, stop when reaching 0
      const newIndex = Math.max(prevIndex - VISIBLE_CARDS, 0);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const targetCard = cardRefs.current[newIndex];
          if (targetCard) {
            targetCard.setAttribute('aria-live', 'assertive');
            targetCard.focus();
            setTimeout(() => {
              targetCard.setAttribute('aria-live', 'off');
            }, 100);
          }
        }, 50);
      });
      return newIndex;
    });
  };

  const focusCardByIndex = (cardIndex: number) => {
    const cardElement = cardRefs.current[cardIndex];
    if (cardElement) {
      // Force blur first, then focus to ensure screen reader announces correctly
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
      setTimeout(() => {
        cardElement.focus();
      }, 50);
    }
  };

  // Enable dragging and buttons only when there are cards to scroll to
  // Check if cards actually fit in the viewport, not just card count
  const canNavigate = hasRemainingCards;
  const canDrag = canNavigate;
  const dragThreshold = cardOffset * 0.25; // need to drag 25% of a card

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canDrag) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging || dragStartX === null) return;
    const delta = e.clientX - dragStartX;

    // Clamp when at boundaries
    if (startIndex === 0 && delta > 0) {
      setDragOffset(delta * 0.35); // resistance
    } else if (startIndex === maxShift && delta < 0) {
      setDragOffset(delta * 0.35);
    } else {
      setDragOffset(delta);
    }
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!isDragging) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setIsDragging(false);

    if (dragOffset <= -dragThreshold) {
      handleNext();
    } else if (dragOffset >= dragThreshold) {
      handlePrev();
    } else {
      // Snap back
      setDragOffset(0);
    }
    setDragStartX(null);
  };

  return (
    <Box
      sx={{
        py: 6
      }}
    >
      {/* Heading aligned with container */}
      <Grid
        container
        justifyContent={'space-between'}
        alignItems="center"
        mb={4}
      >
        <Grid item>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img
              src={getFullImageUrl(carouselData.image.url)}
              alt={carouselData.image.alt}
              style={{ width: '80px', height: '80px', marginRight: '24px' }}
            />
            <Typography
              id={headingId}
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: '28px',
                lineHeight: '36px'
              }}
            >
              {removeHtmlTags(carouselData.heading)}
            </Typography>
          </Box>
        </Grid>
        <Grid
          item
          sx={{
            display: 'flex',
            gap: 0.2,
            backgroundColor: '#FFFFFF !important',
            borderRadius: '999px',
            boxShadow: '0px 0px 15px 0px #00000026'
          }}
        >
          {!(isTablet || isMobile) && canNavigate && (
            <>
              <IconButton
                ref={prevButtonRef}
                aria-label={prevLabel}
                aria-controls={regionId}
                onClick={() => {
                  startIndex > 0 && handlePrev();
                }}
                onKeyDown={(event) => handleNavButtonKeyDown(event, 'prev')}
                sx={{
                  backgroundColor: 'white',
                  boxShadow: '0px 0px 15px 0px #00000026',
                  '&:hover': { backgroundColor: 'white' },
                  '&:focus-visible': {
                    outline: '2px solid black',
                    outlineOffset: '2px'
                  },
                  display: canNavigate ? 'flex' : 'none',
                  padding: { xs: 1, sm: 1 }
                }}
              >
                {startIndex > 0 ? (
                  <img
                    src={`/images/arrows/chevron-right.png`}
                    alt={`Left icon`}
                    style={{ rotate: '180deg' }}
                  ></img>
                ) : (
                  <img
                    src={`/images/arrows/chevron-left.png`}
                    alt={`Left icon`}
                  ></img>
                )}
              </IconButton>
              <IconButton
                ref={nextButtonRef}
                aria-label={nextLabel}
                aria-controls={regionId}
                onClick={() => {
                  startIndex < maxShift && handleNext();
                }}
                onKeyDown={(event) => handleNavButtonKeyDown(event, 'next')}
                sx={{
                  zIndex: 1,
                  backgroundColor: 'white',
                  boxShadow: '0px 0px 15px 0px #00000026',
                  '&:hover': { backgroundColor: 'white' },
                  '&:focus-visible': {
                    outline: '2px solid black',
                    outlineOffset: '2px'
                  },
                  display: canNavigate ? 'flex' : 'none',
                  padding: { xs: 1, sm: 1 }
                }}
              >
                {startIndex < maxShift ? (
                  <img
                    src={`/images/arrows/chevron-right.png`}
                    alt={`Left icon`}
                  ></img>
                ) : (
                  <img
                    src={`/images/arrows/chevron-left.png`}
                    alt={`Left icon`}
                    style={{ rotate: '180deg' }}
                  ></img>
                )}
              </IconButton>
            </>
          )}
        </Grid>
      </Grid>

      {/* Carousel extends beyond container */}
      <Box sx={{ position: 'relative' }}>
        <Box
          ref={scrollContainerRef}
          id={regionId}
          role="region"
          aria-labelledby={headingId}
          aria-live="polite"
          sx={{
            overflow: 'visible'
          }}
        >
          <Box
            role="list"
            aria-label={`${Math.min(
              VISIBLE_CARDS,
              totalCards
            )} of ${totalCards}`}
            sx={{
              display: 'flex',
              transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
              transform: `translateX(calc(-${
                startIndex * cardOffset
              }px + ${dragOffset}px))`,
              width: `${viewportWidth}px`,
              padding: 0,
              margin: 0,
              gap: 1,
              listStyle: 'none',
              userSelect: 'none',
              touchAction: 'pan-y'
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {cardData.map((card, index) => {
              const isFullyVisible =
                index >= startIndex && index < startIndex + VISIBLE_CARDS;
              return (
                <FeatureCard
                  key={card.id}
                  {...card}
                  cardWidth={cardWidth}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                  isFirst={index === 0}
                  isLast={index === totalCards - 1}
                  headingId={headingId}
                  tabIndex={isFullyVisible ? 0 : -1}
                  aria-hidden={!isFullyVisible}
                  onKeyDown={(event) => handleCardKeyDown(event, index)}
                  position={`item ${index + 1}`}
                  total={totalCards}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const FeaturesCarousel: React.FC<FeaturesCarouselProps> = ({ data }) => {
  return (
    <Box>
      {data.map((carouselData) => (
        <SingleCarousel key={carouselData.id} carouselData={carouselData} />
      ))}
    </Box>
  );
};

export default FeaturesCarousel;
